import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { apiInvoices, apiCustomers } from '../../lib/api';
import http from '../../lib/api';
import { fmt, fmtDateUS, invoicePaid, invoiceOutstanding } from '../../lib/invoiceItems';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

import { parseDay } from '../../lib/dates';
// Record a manual payment against an invoice (cash/check/ACH/card-on-file).
export default function PaymentDialog({ open, setOpen, invoice, customer, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('check');
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      const due = invoiceOutstanding(invoice);
      setAmount(String(due > 0 ? due : invoice.total || ''));
      setMethod('check');
      setDate(todayISO());
    }
  }, [open, invoice]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const pid = `PAY-${invoice.number || invoice.id}-${Date.now().toString(36)}`;
      await http.post('/payments', {
        id: pid, invoiceNumber: invoice.number || invoice.id, invoiceId: invoice.id,
        date: fmtDateUS(parseDay(date)), status: 'Success', method, amount: amt,
        customer: customer?.name || invoice.customerName || '',
        customerAccountNumber: customer?.accountNumber || invoice.customerAccountNumber || '',
        customerEmail: customer?.email || invoice.customerEmail || '',
        account: 'Operating Checking', type: 'Deposit',
        desc: `Payment ${method} - ${customer?.name || invoice.customerName || ''}`, matched: true,
      });
      const newPaid = invoicePaid(invoice) + amt;
      const total = Number(invoice.total) || 0;
      const newStatus = newPaid >= total ? 'Paid' : invoice.status;
      await apiInvoices.update(invoice.id, { ...invoice, paid: newPaid, amountDue: Math.max(0, total - newPaid), status: newStatus });
      if (customer) {
        await apiCustomers.update(customer.id, { ...customer, balance: Math.max(0, (Number(customer.balance) || 0) - amt) }).catch(() => {});
      }
      toast.success(`Payment of ${fmt(amt)} recorded`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Could not record payment');
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>{invoice.id} · {customer?.name || invoice.customerName} · Total {fmt(invoice.total)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" data-testid="payment-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="ach">ACH / Bank Transfer</SelectItem>
                <SelectItem value="card">Card on File</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="payment-save-btn" onClick={submit} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">{saving ? 'Saving…' : 'Record Payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
